import { css } from '@emotion/react';
import cns from 'classnames';
import { forwardRef, TextareaHTMLAttributes } from 'react';
import { generateBaseStyles, MapUiSize, MAPUI_SIZES } from '../../common';
import { ContextProps } from './FormControlContext';
import { withForm } from './withForm';

export const TEXTAREA_CLASSNAME = '__mapui_textarea';

export const TEXTAREA_SIZES = MAPUI_SIZES;

const applyMainStyles =
  ({
    isInvalid,
    isVerified,
    isCompleted,
    size,
    isFullWidth,
  }: Pick<
    TextAreaProps,
    'isInvalid' | 'isCompleted' | 'size' | 'isVerified' | 'isFullWidth'
  >) =>
  (theme) => {
    const defaultStyle = css(theme.form.input.defaultStyles);
    const sizesStyle = css(theme.form.input[size]);

    const styles = [
      defaultStyle,
      sizesStyle,
      ...generateBaseStyles({ isFullWidth }),
    ];

    if (isInvalid) {
      styles.push(css(theme.form.input.isInvalid));
    }
    if (isCompleted) {
      styles.push(css(theme.form.input.isCompleted));
    }
    if (isVerified) {
      styles.push(css(theme.form.input.isVerified));
    }

    return css(...styles);
  };

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    ContextProps {
  size?: MapUiSize;
  isFullWidth?: boolean;
}

const InternalTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      size = 'md',
      isFullWidth = false,
      controlId,
      isInvalid,
      isDisabled,
      isVerified,
      isCompleted,
      className,
      rows = 3,
      ...props
    }: TextAreaProps,
    ref
  ) => (
    <textarea
      ref={ref}
      css={applyMainStyles({
        isInvalid,
        isVerified,
        isCompleted,
        size,
        isFullWidth,
      })}
      className={cns(TEXTAREA_CLASSNAME, className)}
      id={controlId}
      disabled={isDisabled}
      rows={rows}
      {...props}
    />
  )
);

InternalTextArea.displayName = 'TextArea';

export const TextArea = withForm<TextAreaProps>(InternalTextArea);
